/**
 * Gravatar utility functions.
 */
// eslint-disable-next-line @typescript-eslint/no-var-requires
const CryptoJS = require('crypto-js');

/**
 * Generate Gravatar URL from email address.
 * @param email - User's email address
 * @param size - Image size in pixels (default: 80)
 * @param defaultType - Default image type if no Gravatar exists
 *                      Use 'initials' to show initials-based avatar
 *                      Other options: '404' (returns 404), 'mp' (mystery person), 'identicon', 'monsterid', 'wavatar', 'retro', 'robohash', 'blank'
 * @param name - User's name (used with d=initials to generate initials)
 * @returns Gravatar URL
 */
export function getGravatarUrl(
  email: string,
  size: number = 80,
  defaultType: string = 'initials',
  name?: string
): string {
  if (!email) {
    const params = new URLSearchParams({ s: size.toString(), d: defaultType });
    if (defaultType === 'initials' && name) {
      params.append('name', name);
    }
    return `https://www.gravatar.com/avatar/?${params.toString()}`;
  }

  // Normalize email: lowercase and trim (Gravatar requirement)
  const normalizedEmail = email.toLowerCase().trim();
  
  // Generate MD5 hash of the email
  const hash = CryptoJS.MD5(normalizedEmail).toString();
  
  // Build URL with parameters
  const params = new URLSearchParams({ s: size.toString(), d: defaultType });
  if (defaultType === 'initials' && name) {
    params.append('name', name);
  }
  
  // Gravatar URL format: https://www.gravatar.com/avatar/{hash}?s={size}&d={default}&name={name}
  // Using d=initials makes Gravatar generate an initials-based avatar when no custom avatar exists
  return `https://www.gravatar.com/avatar/${hash}?${params.toString()}`;
}

/**
 * Get Gravatar URL with fallback to initials.
 * Uses 'initials' as default so Gravatar generates initials-based avatars automatically.
 * @param email - User's email address
 * @param size - Image size in pixels (default: 80)
 * @param name - User's name (optional, used to generate initials)
 * @returns Gravatar URL
 */
export function getGravatarUrlWithFallback(
  email: string,
  size: number = 80,
  name?: string
): string {
  return getGravatarUrl(email, size, 'initials', name);
}

